import { addMilliseconds, parse } from "date-fns";
import { format, getTimezoneOffset, formatInTimeZone } from "date-fns-tz";
import { sortBy } from "lodash";

import {
  DataExportMethod,
  type FetchScheduledExports_me_scheduledExports_data_schedules,
} from "@north-beam/nb-client-side";
import { DataExportFrequency } from "@north-beam/nb-client-side";

export function formatCadence(
  frequency: DataExportFrequency,
  schedules: FetchScheduledExports_me_scheduledExports_data_schedules[],
) {
  const sortedSchedules = sortBy(schedules, ["localDay", "localTime"]);

  let out = "";

  if (frequency === DataExportFrequency.DAILY) {
    const scheduleString = sortedSchedules.map((schedule) => {
      const { hour } = extractDateParts({
        frequency,
        schedule,
      });
      return hour;
    });

    out = `Daily at ${scheduleString.join(", ")}`;
  } else if (frequency === DataExportFrequency.WEEKLY) {
    const scheduleString = sortedSchedules.map((schedule) => {
      const { hour, dayOfWeek } = extractDateParts({
        frequency,
        schedule,
      });
      return `${dayOfWeek} at ${hour}`;
    });

    out = `Weekly on ${scheduleString.join(", ")}`;
  } else {
    const scheduleString = sortedSchedules.map((schedule) => {
      const { hour, dayOfMonth } = extractDateParts({
        frequency,
        schedule,
      });
      return `${dayOfMonth} day of the month at ${hour}`;
    });

    out = `Monthly on ${scheduleString.join(", ")}`;
  }

  return out;
}

function extractDateParts({
  frequency,
  schedule,
  useMachineTimeZone = true,
}: {
  frequency: DataExportFrequency;
  schedule: FetchScheduledExports_me_scheduledExports_data_schedules;
  useMachineTimeZone?: boolean;
}) {
  const {
    localDay,
    localTime: scheduleTime,
    timeZone: scheduleTimeZone,
  } = schedule;

  const machineTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const machineOffsetToUTC = getTimezoneOffset(machineTimeZone);
  const scheduleOffsetToUTC = getTimezoneOffset(scheduleTimeZone);
  const scheduleOffsetToMachineTime = machineOffsetToUTC - scheduleOffsetToUTC;

  const scheduleTimeWithoutCorrectOffset = parse(
    scheduleTime,
    "HH:mm:ss",
    new Date(),
  );

  const scheduleTimeInLocalTimeZone = addMilliseconds(
    scheduleTimeWithoutCorrectOffset,
    scheduleOffsetToMachineTime,
  );

  const timeZoneToOutput = useMachineTimeZone
    ? machineTimeZone
    : scheduleTimeZone;

  const hour = formatInTimeZone(
    scheduleTimeInLocalTimeZone,
    timeZoneToOutput,
    "haaa",
  );
  const tzAbbreviation = format(scheduleTimeInLocalTimeZone, "z", {
    timeZone: timeZoneToOutput,
  });

  let dayOfWeek = "";
  let dayOfMonth = "";

  if (frequency === DataExportFrequency.WEEKLY) {
    dayOfWeek = format(parse(localDay, "i", new Date()), "EEEE");
  }

  if (frequency === DataExportFrequency.MONTHLY) {
    dayOfMonth =
      localDay === "-1"
        ? "last"
        : format(parse(localDay, "d", new Date()), "do");
  }

  return { hour, dayOfWeek, dayOfMonth, tzAbbreviation };
}

const exportMethodToDestinationNameMapping = {
  [DataExportMethod.NORTHBEAM_GCS_BUCKET]: "Northbeam Documents",
  [DataExportMethod.EXTERNAL_GCS_BUCKET]: "GCS",
  [DataExportMethod.EXTERNAL_S3_BUCKET]: "S3",
};
export const getDestinationNameFromExportMethod = (
  exportMethod: DataExportMethod,
) => exportMethodToDestinationNameMapping[exportMethod];
